import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Building2, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAgency } from '@/auth/AgencyProvider';
import { useMyInvites } from '@/data/useMyInvites';
import { useMyRemovedMemberships } from '@/data/useMyRemovedMemberships';
import { SelfCreateAgency } from '@/components/billing/SelfCreateAgency';

/**
 * A signed-in account that is not an ACTIVE member of any agency: typically a driver, who uses the
 * mobile app. Rendered in the main area; the rail (and its sign-out) stays.
 *
 * An invitee who signed in without opening her invite link lands here too: her pending office
 * invitations (`my_member_invites`, no agency needed) are listed, each linking to its invite page.
 * No rows, or a failed read: the plain copy (the read is a courtesy, never an error wall).
 *
 * F62: a member an admin deactivated lands here too (no active membership). `my_removed_memberships` names
 * the agency: "Your access to X was removed", never the driver copy.
 */
export function NoAgencyScreen() {
  const { t } = useTranslation();
  const { invites } = useMyInvites();
  const { removed } = useMyRemovedMemberships();
  const pending = invites.length > 0;
  const gone = removed[0] ?? null;
  const title = gone
    ? removed.length > 1
      ? t('agency.removed.titleMany')
      : t('agency.removed.title', { agency: gone.agencyName })
    : pending
      ? t('agency.pendingInvites.title')
      : t('agency.noAgencyTitle');
  const body = gone
    ? removed.length > 1
      ? t('agency.removed.bodyMany', { agencies: removed.map((r) => r.agencyName).join(', ') })
      : t('agency.removed.body', { agency: gone.agencyName })
    : pending
      ? t('agency.pendingInvites.body')
      : t('agency.noAgencyBody');
  return (
    <Card className="mx-auto max-w-2xl">
      <CardContent className="flex flex-col items-center gap-4 p-12 text-center">
        <span className="grid size-12 place-items-center rounded-full bg-accent-wash">
          <Building2 className="size-6 text-accent-base" aria-hidden="true" />
        </span>
        <h1 className="text-2xl font-semibold text-ink">{title}</h1>
        <p className="text-base text-ink-secondary">{body}</p>
        {pending && (
          <ul className="flex w-full flex-col gap-3 text-left">
            {invites.map((invite) => {
              const values = { agency: invite.agencyName, role: t(`roles.${invite.role}`), name: invite.inviterName ?? '' };
              return (
                <li
                  key={invite.inviteId}
                  className="flex flex-col gap-3 rounded-md border border-hairline p-4 sm:flex-row sm:items-center"
                >
                  <p className="min-w-0 flex-1 text-base text-ink">
                    {invite.inviterName
                      ? t('agency.pendingInvites.line', values)
                      : t('agency.pendingInvites.lineNoInviter', values)}
                  </p>
                  <Button asChild size="sm">
                    <Link to={`/invite/${invite.inviteId}`}>{t('agency.pendingInvites.open')}</Link>
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
        {/* agency-billing F5: only while self sign-up is on (it ships OFF); an invitee joins through the invite. */}
        {!pending && <SelfCreateAgency />}
      </CardContent>
    </Card>
  );
}

/**
 * F64: a signed-in member with a pending invitation to ANOTHER agency sees it on every screen, with the link
 * to its invite page (where it is accepted). An invitation to an agency the member is already in is not
 * offered; a failed read shows nothing.
 */
export function PendingInvitesBanner() {
  const { t } = useTranslation();
  const agency = useAgency();
  const { invites } = useMyInvites();
  const mine = new Set(agency.status === 'ready' ? agency.memberships.map((m) => m.agencyId) : []);
  const others = invites.filter((i) => !mine.has(i.agencyId));
  if (others.length === 0) return null;
  return (
    <section aria-label={t('agency.pendingInvites.banner')} className="mb-4 flex flex-col gap-2 rounded-md border border-hairline bg-card px-4 py-3 shadow-card">
      {others.map((invite) => {
        const values = { agency: invite.agencyName, role: t(`roles.${invite.role}`), name: invite.inviterName ?? '' };
        return (
          <div key={invite.inviteId} className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <p className="min-w-0 flex-1 text-base text-ink">
              {invite.inviterName ? t('agency.pendingInvites.line', values) : t('agency.pendingInvites.lineNoInviter', values)}
            </p>
            <Button asChild size="sm">
              <Link to={`/invite/${invite.inviteId}`}>{t('agency.pendingInvites.open')}</Link>
            </Button>
          </div>
        );
      })}
    </section>
  );
}

/** The membership read failed: the shell still renders, gated actions stay hidden, a retry is offered. */
export function AgencyErrorBanner({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = React.useState(false);
  if (dismissed) return null;
  return (
    <div
      role="alert"
      className="mb-4 flex items-center gap-3 rounded-md border border-hairline bg-card px-4 py-3 text-base text-danger shadow-card"
    >
      <p className="min-w-0 flex-1">{t('agency.loadError')}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('agency.retry')}
      </Button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('common.dismiss')}
        title={t('common.dismiss')}
        className="grid size-8 place-items-center rounded-md text-ink-muted hover:bg-secondary"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}
